/**
 * CMS hooks — TanStack Query wrappers for media CRUD operations.
 *
 * IMPORTANT: useUploadMedia uses raw fetch with FormData, NOT the api() helper.
 * The api() helper hardcodes Content-Type: application/json which breaks multipart uploads.
 * Raw fetch lets the browser set the correct multipart/form-data boundary automatically.
 *
 * Hooks:
 * - useMedia()          — paginated list of media records
 * - useUploadMedia()    — mutation to upload a file via multipart/form-data
 * - useDeleteMedia()    — mutation to delete a media file by ID
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiDelete, apiGet, apiPut, ApiError } from '@/lib/api'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MediaItem {
  id: string
  filename: string
  mime_type: string
  file_size: number
  url: string
  alt: string | null
  width: number | null
  height: number | null
  uploaded_by_id: string | null
  created_at: string
}

interface MediaListResponse {
  data: MediaItem[]
}

interface MediaUploadResponse {
  data: MediaItem
}

// ─── Query keys ───────────────────────────────────────────────────────────────

export const mediaKeys = {
  all: ['media'] as const,
  list: () => [...mediaKeys.all, 'list'] as const,
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Fetch the list of media records, newest first.
 */
export function useMedia() {
  return useQuery<MediaItem[]>({
    queryKey: mediaKeys.list(),
    queryFn: async () => {
      const res = await fetch('/api/v1/media', {
        credentials: 'include',
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new ApiError(res.status, body.error || res.statusText)
      }
      const json: MediaListResponse = await res.json()
      return json.data
    },
  })
}

/**
 * Mutation to upload a single file via multipart/form-data.
 *
 * Uses raw fetch — NOT the api() helper — because the api() helper sets
 * Content-Type: application/json which prevents the browser from setting
 * the correct multipart/form-data boundary for file uploads.
 *
 * On success: invalidates the media list cache.
 */
export function useUploadMedia() {
  const queryClient = useQueryClient()

  return useMutation<MediaItem, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)

      // Raw fetch — do NOT use api() helper here (it sets Content-Type: application/json)
      const res = await fetch('/api/v1/media', {
        method: 'POST',
        credentials: 'include',
        body: formData,
        // No Content-Type header — browser sets it with boundary automatically
      })

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }))
        throw new ApiError(res.status, body.error || res.statusText)
      }

      const json: MediaUploadResponse = await res.json()
      return json.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.all })
    },
  })
}

/**
 * Mutation to delete a media file by ID.
 * On success: invalidates the media list cache.
 */
export function useDeleteMedia() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (id: string) => {
      await apiDelete(`/api/v1/media/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.all })
    },
  })
}

// ─── Globals hooks (Phase 18-02) ──────────────────────────────────────────────

// ── Globals types ─────────────────────────────────────────────────────────────

export interface GlobalRow {
  id: string
  slug: string
  data: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface GlobalLocaleData {
  id: string
  slug: string
  locale: string
  /** Neutral (locale-agnostic) data — base layer */
  neutral_data: Record<string, unknown>
  /** Locale-specific data — overrides neutral_data */
  locale_data: Record<string, unknown>
  /** Merged result: neutral_data merged with locale_data (locale wins) */
  merged: Record<string, unknown>
}

export type SupportedLocale = 'es' | 'en' | 'pt'

// ── Globals API response wrappers ─────────────────────────────────────────────

interface GlobalsListApiResponse {
  data: GlobalRow[]
}

interface GlobalLocaleApiResponse {
  data: GlobalLocaleData
}

interface UpsertLocaleApiResponse {
  data: {
    id: string
    global_id: string
    locale: string
    data: Record<string, unknown>
    created_at: string
    updated_at: string
  }
}

// ── Globals query key factory ─────────────────────────────────────────────────

export const cmsKeys = {
  all: ['cms'] as const,
  globals: () => [...cmsKeys.all, 'globals'] as const,
  globalLocale: (slug: string, locale: string) =>
    [...cmsKeys.all, 'global', slug, locale] as const,
}

// ── Globals hooks ─────────────────────────────────────────────────────────────

/**
 * Fetch all globals (list view).
 * Returns the 4 known global slugs with their neutral data.
 */
export function useGlobals() {
  return useQuery<GlobalRow[]>({
    queryKey: cmsKeys.globals(),
    queryFn: async () => {
      const res = await apiGet<GlobalsListApiResponse>('/api/v1/globals')
      return res.data
    },
  })
}

/**
 * Fetch a single global's data merged with locale-specific overrides.
 *
 * Disabled when slug or locale is falsy.
 * Returns { merged, neutral_data, locale_data } — use merged for the editor.
 */
export function useGlobalLocale(slug: string, locale: SupportedLocale) {
  return useQuery<GlobalLocaleData>({
    queryKey: cmsKeys.globalLocale(slug, locale),
    queryFn: async () => {
      const res = await apiGet<GlobalLocaleApiResponse>(
        `/api/v1/globals/${slug}/locales/${locale}`,
      )
      return res.data
    },
    enabled: !!slug && !!locale,
  })
}

/**
 * Mutation to save (upsert) locale-specific data for a global.
 *
 * On success, invalidates the specific global+locale query so the next
 * read fetches fresh merged data.
 */
export function useSaveGlobalLocale() {
  const queryClient = useQueryClient()

  return useMutation<
    UpsertLocaleApiResponse['data'],
    Error,
    { slug: string; locale: SupportedLocale; data: Record<string, unknown> }
  >({
    mutationFn: async ({ slug, locale, data }) => {
      const res = await apiPut<UpsertLocaleApiResponse>(
        `/api/v1/globals/${slug}/locales/${locale}`,
        { data },
      )
      return res.data
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cmsKeys.globalLocale(variables.slug, variables.locale),
      })
    },
  })
}

/**
 * Save neutral (locale-agnostic) data for a global.
 * Used for fields like contact-settings, social-proof metrics, hub-standalone tiers.
 */
export function useSaveGlobalNeutral() {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    Error,
    { slug: string; data: Record<string, unknown> }
  >({
    mutationFn: async ({ slug, data }) => {
      const res = await apiPut<{ data: unknown }>(
        `/api/v1/globals/${slug}`,
        { data },
      )
      return res.data
    },
    onSuccess: (_data, variables) => {
      // Invalidate all locale queries for this global since neutral data changed
      queryClient.invalidateQueries({
        queryKey: ['global-locale', variables.slug],
      })
    },
  })
}
